import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Badge,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import {
  TimeIcon,
  CheckCircleIcon,
  UnlockIcon,
  SettingsIcon,
} from "@chakra-ui/icons";
import React from "react";

type WhatIsWobbleSectionProps = {
  onPrimaryCtaHref?: string;
  onSecondaryCtaHref?: string;
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-6px); }
  100% { transform: translateY(0px); }
`;

const WhatIsWobbleSection: React.FC<WhatIsWobbleSectionProps> = ({
  onPrimaryCtaHref = "/signup",
  onSecondaryCtaHref = "/video",
}) => {
  const cardBg = useColorModeValue("whiteAlpha.800", "whiteAlpha.100");
  const textMuted = useColorModeValue("gray.600", "gray.300");
  const accent = useColorModeValue("blue.600", "blue.300");

  return (
    <Box
      as="section"
      position="relative"
      overflow="hidden"
      py={{ base: 14, md: 24 }}
      px={{ base: 4, md: 8 }}
      _before={{
        content: '""',
        position: "absolute",
        inset: "-20%",
        bgGradient:
          "radial( at 20% 20%, rgba(59,130,246,0.18), transparent 55% ), radial( at 80% 30%, rgba(99,102,241,0.18), transparent 55% ), radial( at 50% 90%, rgba(16,185,129,0.16), transparent 50% )",
        filter: "blur(40px)",
      }}
    >
      {/* blur background color */}
      <Box
        position="absolute"
        top="10%"
        right="5%"
        w="320px"
        h="320px"
        bgGradient="radial(circle at 30% 30%, rgba(99,102,241,0.25), transparent 60%)"
        filter="blur(18px)"
        animation={`${float} 6s ease-in-out infinite`}
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="0"
        left="0"
        w="280px"
        h="280px"
        bgGradient="radial(circle at 70% 70%, rgba(16,185,129,0.25), transparent 60%)"
        filter="blur(16px)"
        animation={`${float} 7s ease-in-out infinite`}
        pointerEvents="none"
      />

      <Flex
        maxW="7xl"
        mx="auto"
        align="center"
        gap={{ base: 10, md: 16 }}
        direction={{ base: "column", md: "row" }}
        animation={`${fadeIn} 0.7s ease-out both`}
      >
        {/* Left side */}
        <VStack align="start" spacing={6} flex="1">
          <Badge
            px={3}
            py={1}
            borderRadius="full"
            bg="blue.50"
            color={accent}
            fontWeight="semibold"
          >
            Wobble - Online időpontfoglalás egyszerűen
          </Badge>

          <Heading as="h2" lineHeight="1.1" size={{ base: "xl", md: "2xl" }}>
            Szabadság az idődben.{" "}
            <Box
              as="span"
              bgGradient="linear(to-r, teal.400, blue.500)"
              bgClip="text"
            >
              Kevesebb egyeztetés.
            </Box>{" "}
            Hagyd, hogy az ügyfelek maguk foglaljanak.
          </Heading>

          <Text as="h3" fontSize={{ base: "md", md: "lg" }} color={textMuted}>
            A <strong>Wobble</strong> egy könnyen kezelhető, ügyfélbarát{" "}
            <em>online időpontfoglaló rendszer</em>, amellyel az ügyfeleid
            <strong> 0-24</strong> foglalhatnak. Nincs több felesleges
            telefonálás, egyeztetés vagy Excel - a foglalás, értesítés és
            időpontkezelés <strong>automatikusan</strong> működik.
          </Text>

          <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} pt={2}>
            <HStack
              bg={cardBg}
              backdropFilter="blur(10px)"
              borderRadius="xl"
              p={4}
              spacing={3}
              border="1px solid"
              borderColor="blackAlpha.100"
            >
              <Icon as={TimeIcon} boxSize={5} color={accent} />
              <Text fontWeight="medium">Időt spórol - foglalás 0-24</Text>
            </HStack>

            <HStack
              bg={cardBg}
              backdropFilter="blur(10px)"
              borderRadius="xl"
              p={4}
              spacing={3}
              border="1px solid"
              borderColor="blackAlpha.100"
            >
              <Icon as={SettingsIcon} boxSize={5} color={accent} />
              <Text fontWeight="medium">
                Kevesebb egyeztetés - automatizált értesítések
              </Text>
            </HStack>

            <HStack
              bg={cardBg}
              backdropFilter="blur(10px)"
              borderRadius="xl"
              p={4}
              spacing={3}
              border="1px solid"
              borderColor="blackAlpha.100"
            >
              <Icon as={UnlockIcon} boxSize={5} color={accent} />
              <Text fontWeight="medium">
                Stresszmentes szervezés - minden egy helyen
              </Text>
            </HStack>

            <HStack
              bg={cardBg}
              backdropFilter="blur(10px)"
              borderRadius="xl"
              p={4}
              spacing={3}
              border="1px solid"
              borderColor="blackAlpha.100"
            >
              <Icon as={CheckCircleIcon} boxSize={5} color={accent} />
              <Text fontWeight="medium">
                Egyéni és csoportos foglalások kezelése
              </Text>
            </HStack>
          </SimpleGrid>

          <HStack spacing={4} pt={2} wrap="wrap">
            <Button
              as="a"
              href={onPrimaryCtaHref}
              size="lg"
              colorScheme="blue"
              px={6}
              textDecoration="none"
              _hover={{ bg: "blue.400", textDecoration: "none" }}
            >
              Próbáld ki 30 napig ingyen
            </Button>
            <Button
              as="a"
              href={onSecondaryCtaHref}
              size="lg"
              variant="outline"
              borderColor={"blue.400"}
              textDecoration="none"
              _hover={{ bg: "white", textDecoration: "none" }}
            >
              Videós útmutató
            </Button>
          </HStack>

          <HStack spacing={4} pt={1} color={textMuted} fontSize="sm">
            <Text>Nincs bonyolult bevezetés</Text>•
            <Text>Percek alatt kész</Text>•<Text>Magyar felület</Text>
          </HStack>
        </VStack>

        <Box
          flex="1"
          w="100%"
          maxW="560px"
          bg={cardBg}
          border="1px solid"
          borderColor="blackAlpha.100"
          backdropFilter="blur(12px)"
          borderRadius="2xl"
          boxShadow="xl"
          p={{ base: 6, md: 8 }}
        >
          <VStack align="start" spacing={4}>
            <Heading as="h3" size="md">
              Mi a Wobble?
            </Heading>
            <Text color={textMuted}>
              Egy <strong>időpontfoglaló rendszer és online megjelenés </strong>
              szolgáltatóknak: saját foglalási oldal és/vagy weboldal,
              automatikus e-mail értesítések, naptárszinkron és reszponzív,
              gyors ügyfélélmény, egyedi kérdőív, számlázás.
            </Text>
            <Box mt={2} p={4} borderRadius="xl" bg="blackAlpha.50">
              <Text color={textMuted}>
                Eleged van a nonstop telefonhívásokból és üzenetekből, amik csak
                az egyeztetéssel telnek? Hagyd, hogy a Wobble automatikusan
                kezelje a foglalásokat, informálja az ügyfeleket, és visszaadja
                az értékes idődet.{" "}
              </Text>
            </Box>
            <HStack spacing={3} pt={1} wrap="wrap">
              <Badge colorScheme="blue" variant="subtle">
                0-24 foglalás
              </Badge>
              <Badge colorScheme="green" variant="subtle">
                Automatikus értesítések
              </Badge>
              <Badge colorScheme="purple" variant="subtle">
                Egyéni & csoportos
              </Badge>
              <Badge colorScheme="gray" variant="subtle">
                Naptár szinkron
              </Badge>
              <Badge colorScheme="yellow" variant="subtle">
                Számlázás
              </Badge>
              <Badge colorScheme="red" variant="subtle">
                Előlegfizetés
              </Badge>
              <Badge colorScheme="teal" variant="subtle">
                Weboldal + Blog
              </Badge>
              <Badge colorScheme="orange" variant="subtle">
                Várólista
              </Badge>
              <Badge colorScheme="pink" variant="subtle">
                Krédőív
              </Badge>
              <Badge colorScheme="blackAlpha" variant="subtle">
                Kategóriák
              </Badge>
              <Badge colorScheme="cyan" variant="subtle">
                Ügyfélfiók
              </Badge>
            </HStack>
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
};

export default WhatIsWobbleSection;
